import { Typography, CircularProgress } from '@mui/material'
import { Box } from '@mui/system'
import React, { useEffect, useState } from 'react'
import { getOrders, OrderDetails } from 'src/common/api'
import { OrderStatus } from 'src/common/static'
import { alertNotification } from 'src/ui'

interface MintBurnStats {
  completedMints: number
  initiatedAndCompletedMints: number
  completedBurns: number
  initiatedAndCompletedBurns: number
}

const MintBurnSummary: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<MintBurnStats>({
    completedMints: 0,
    initiatedAndCompletedMints: 0,
    completedBurns: 0,
    initiatedAndCompletedBurns: 0
  })

  useEffect(() => {
    const fetchOrderStats = async () => {
      try {
        setLoading(true)
        // Fetch all orders without pagination to get complete statistics
        const response = await getOrders()
        const orders = response.data.orders
        const stats = calculateStats(orders)
        setStats(stats)
      } catch (err) {
        alertNotification('Failed to fetch mint/burn statistics. Please try again later.', 'error')
        console.error('Error fetching orders:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchOrderStats()
  }, [])

  const calculateStats = (orders: OrderDetails[]): MintBurnStats => {
    // Include both enum values and raw API string values
    const completed = [OrderStatus.PROCESSED, 'PROCESSED']
    const initiatedAndCompleted = [
      OrderStatus.INITIATED,
      OrderStatus.QUEUED,
      OrderStatus.PROCESSED,
      'INITIATED',
      'QUEUED',
      'PROCESSED'
    ]

    // Sum crypto amounts instead of counting orders
    const completedMints = orders
      .filter((order) => order.type === 'MINT' && completed.includes(order.status))
      .reduce((sum, order) => sum + parseFloat(order.cryptoAmount || '0'), 0)

    const initiatedAndCompletedMints = orders
      .filter((order) => order.type === 'MINT' && initiatedAndCompleted.includes(order.status))
      .reduce((sum, order) => sum + parseFloat(order.cryptoAmount || '0'), 0)

    const completedBurns = orders
      .filter((order) => order.type === 'BURN' && completed.includes(order.status))
      .reduce((sum, order) => sum + parseFloat(order.cryptoAmount || '0'), 0)

    const initiatedAndCompletedBurns = orders
      .filter((order) => order.type === 'BURN' && initiatedAndCompleted.includes(order.status))
      .reduce((sum, order) => sum + parseFloat(order.cryptoAmount || '0'), 0)

    return {
      completedMints,
      initiatedAndCompletedMints,
      completedBurns,
      initiatedAndCompletedBurns
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={40} />
      </Box>
    )
  }

  return (
    <Box height="100%">
      <Typography variant="h6" fontWeight={500} mb={3}>
        Mint/Burn Summary
      </Typography>
      <Box display="flex" justifyContent="space-between" gap={4}>
        <Box>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Total Tokens Minted
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {stats.completedMints.toLocaleString()}
            <Typography component="span" variant="h4" fontWeight={400} color="text.secondary" ml={0.5}>
              /{stats.initiatedAndCompletedMints.toLocaleString()}
            </Typography>
          </Typography>
        </Box>
        <Box>
          <Typography variant="body2" color="text.secondary" mb={1}>
            Total Tokens Burnt
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {stats.completedBurns.toLocaleString()}
            <Typography component="span" variant="h4" fontWeight={400} color="text.secondary" ml={0.5}>
              /{stats.initiatedAndCompletedBurns.toLocaleString()}
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default MintBurnSummary
